import React, {
  useState, useEffect, useRef, useCallback,
} from "react";
import axios from "axios";
import localforage from "localforage";
import { io } from "socket.io-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane, faPlus, faUsers, faUser, faTimes,
  faSearch, faPaperclip, faFile, faFilePdf, faFileWord,
  faFileExcel, faImage, faCheck, faCheckDouble,
  faDiagramProject, faSpinner, faCircle, faXmark,
  faUserGroup, faPencil, faTrash, faArrowDown,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/chatsTab.css";

const API_URL  = process.env.REACT_APP_API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_URL?.replace("/api", "");

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (mimeType) => {
  if (!mimeType) return faFile;
  if (mimeType.startsWith("image/")) return faImage;
  if (mimeType === "application/pdf") return faFilePdf;
  if (mimeType.includes("word")) return faFileWord;
  if (mimeType.includes("excel") || mimeType.includes("sheet")) return faFileExcel;
  return faFile;
};

const getInitials = (email) => {
  if (!email) return "?";
  const parts = email.split("@")[0].split(/[._-]/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
};

const getAvatarColor = (str) => {
  const colors = ["#CE452B", "#1565C0", "#2E7D32", "#7B1FA2", "#E65100", "#00695C", "#C62828", "#4527A0"];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ email, size = 36, isGroup = false }) => {
  const color = getAvatarColor(email || "group");
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      backgroundColor: isGroup ? "#E0E0E0" : color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: size * 0.35, color: isGroup ? "#666" : "#fff",
      flexShrink: 0,
    }}>
      {isGroup
        ? <FontAwesomeIcon icon={faUsers} style={{ fontSize: size * 0.4 }} />
        : getInitials(email)}
    </div>
  );
};

// ── Online dot ────────────────────────────────────────────────────────────────
const OnlineDot = ({ online }) => (
  <span style={{
    width: 9, height: 9, borderRadius: "50%",
    backgroundColor: online ? "#2E7D32" : "#CCC",
    display: "inline-block", flexShrink: 0,
    border: "1.5px solid #fff",
  }} />
);

// ── Task Notification Card ────────────────────────────────────────────────────
const TaskNotificationCard = ({ message, onViewTask }) => (
  <div className="task-notif-card">
    <div className="task-notif-icon">
      <FontAwesomeIcon icon={faDiagramProject} />
    </div>
    <div className="task-notif-body">
      <div className="task-notif-label">Task Assignment</div>
      <div className="task-notif-title">{message.taskTitle}</div>
      <div className="task-notif-from">from {message.sender}</div>
    </div>
    <button className="task-notif-btn" onClick={() => onViewTask && onViewTask(message.taskRef)}>
      View Task
    </button>
  </div>
);

// ── File Bubble ───────────────────────────────────────────────────────────────
const FileBubble = ({ message, isMine }) => {
  const isImage = message.fileMimeType?.startsWith("image/");
  return (
    <div className={`file-bubble ${isMine ? "mine" : "theirs"}`}>
      {isImage ? (
        <a href={message.fileUrl} target="_blank" rel="noreferrer">
          <img src={message.fileUrl} alt={message.fileName} className="file-bubble-image" />
        </a>
      ) : (
        <a href={message.fileUrl} target="_blank" rel="noreferrer" className="file-bubble-link">
          <div className="file-bubble-icon">
            <FontAwesomeIcon icon={getFileIcon(message.fileMimeType)} />
          </div>
          <div className="file-bubble-meta">
            <div className="file-bubble-name">{message.fileName}</div>
            <div className="file-bubble-size">{formatFileSize(message.fileSize)}</div>
          </div>
        </a>
      )}
    </div>
  );
};

// ── Message Bubble ────────────────────────────────────────────────────────────
const MessageBubble = ({ message, isMine, showSender, onDelete, onViewTask }) => {
  const [hovered, setHovered] = useState(false);

  if (message.type === "task_notification") {
    return (
      <div className={`bubble-row ${isMine ? "mine" : "theirs"}`}>
        <TaskNotificationCard message={message} onViewTask={onViewTask} />
        <div className="bubble-time">{formatTime(message.createdAt)}</div>
      </div>
    );
  }

  return (
    <div
      className={`bubble-row ${isMine ? "mine" : "theirs"}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!isMine && showSender && (
        <div className="bubble-sender">{message.sender}</div>
      )}
      {/* CHANGE 5a — add bubble-pending class when message is optimistic */}
      <div className={`bubble ${isMine ? "bubble-mine" : "bubble-theirs"} ${message._pending ? "bubble-pending" : ""}`}>
        {message.type === "file"
          ? <FileBubble message={message} isMine={isMine} />
          : <span className="bubble-text">{message.content}</span>}
      </div>
      <div className="bubble-meta-row">
        {/* CHANGE 5b — show "Sending..." for pending messages */}
        <span className="bubble-time">
          {message._pending ? "Sending..." : formatTime(message.createdAt)}
        </span>
        {isMine && !message._pending && message.readBy?.length > 1 && (
          <FontAwesomeIcon icon={faCheckDouble} className="read-tick" />
        )}
      </div>
      {hovered && isMine && !message._pending && onDelete && (
        <button className="bubble-delete-btn" onClick={() => onDelete(message._id)} title="Delete">
          <FontAwesomeIcon icon={faTrash} />
        </button>
      )}
    </div>
  );
};

// ── New Group Modal ───────────────────────────────────────────────────────────
const NewGroupModal = ({ onClose, onCreated, allEmployees, currentEmail }) => {
  const [name, setName]           = useState("");
  const [search, setSearch]       = useState("");
  const [selected, setSelected]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  const filtered = allEmployees.filter(
    (e) => e !== currentEmail &&
      e.toLowerCase().includes(search.toLowerCase()) &&
      !selected.includes(e)
  );

  const handleCreate = async () => {
    if (!name.trim()) return setError("Group name is required.");
    if (!selected.length) return setError("Add at least one member.");
    setLoading(true);
    try {
      const token = await localforage.getItem("token");
      const res = await axios.post(
        `${API_URL}/chat/conversations/group`,
        { name, members: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(res.data.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create group.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            <FontAwesomeIcon icon={faUserGroup} style={{ marginRight: 8, color: "var(--primary-color)" }} />
            New Group
          </h3>
          <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="form-group">
          <label>Group Name</label>
          <input value={name} onChange={(e) => { setName(e.target.value); setError(""); }} placeholder="e.g. DRP Team" />
        </div>

        <div className="form-group">
          <label>Add Members</label>
          <div style={{ position: "relative" }}>
            <FontAwesomeIcon icon={faSearch} className="modal-search-icon" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email..."
              style={{ paddingLeft: 32 }}
            />
          </div>
          {filtered.length > 0 && (
            <div className="modal-dropdown">
              {filtered.slice(0, 6).map((e) => (
                <div key={e} className="modal-dropdown-item" onClick={() => setSelected([...selected, e])}>
                  <Avatar email={e} size={28} />
                  <span>{e}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected.length > 0 && (
          <div className="modal-chips">
            {selected.map((e) => (
              <span key={e} className="modal-chip">
                {e}
                <button onClick={() => setSelected(selected.filter((s) => s !== e))}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
          </div>
        )}

        <button
          className="btn btn-primary"
          style={{ width: "100%", borderRadius: 10, marginTop: 8 }}
          onClick={handleCreate}
          disabled={loading}
        >
          {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Create Group"}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN CHATS TAB
// ─────────────────────────────────────────────────────────────────────────────
const ChatsTab = ({ currentUserEmail, allEmployees, onViewTask }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId]   = useState(null);
  const [messages, setMessages]           = useState([]);
  const [hasMore, setHasMore]             = useState(false);
  const [oldestId, setOldestId]           = useState(null);
  const [loadingMsgs, setLoadingMsgs]     = useState(false);

  const [inputText, setInputText]         = useState("");
  const [uploading, setUploading]         = useState(false);
  const [typingUsers, setTypingUsers]     = useState({});
  const [onlineEmails, setOnlineEmails]   = useState([]);
  const [convSearch, setConvSearch]       = useState("");
  const [showNewGroup, setShowNewGroup]   = useState(false);
  const [sidebarOpen, setSidebarOpen]     = useState(true);

  const socketRef      = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef   = useRef(null);
  const typingTimer    = useRef(null);
  // CHANGE 1 — ref to mirror activeConvId for use inside socket callbacks
  const activeConvRef  = useRef(null);

  // Keep ref in sync with state
  useEffect(() => { activeConvRef.current = activeConvId; }, [activeConvId]);

  const getToken = useCallback(async () => {
    return await localforage.getItem("token");
  }, []);

  // ── Fetch conversations ───────────────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    try {
      const tok = await getToken();
      if (!tok) return;
      const res = await axios.get(`${API_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setConversations(res.data.data || []);
    } catch (err) {
      console.error("fetchConversations:", err.message);
    }
  }, [getToken]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // ── Socket.io connection ──────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUserEmail) return;

    let socketInstance = null;

    localforage.getItem("token").then((tok) => {
      if (!tok) return;

      socketInstance = io(SOCKET_URL, {
        auth: { email: currentUserEmail, token: tok },
        transports: ["websocket"],
        reconnectionAttempts: 5,
      });

      socketRef.current = socketInstance;

      socketInstance.on("connect", () => {
        console.log("[Socket] Connected:", socketInstance.id);
        // Re-join active room on reconnect so messages never drop
        if (activeConvRef.current) {
          socketInstance.emit("join_room", { conversationId: activeConvRef.current });
        }
      });

      socketInstance.on("connect_error", (err) =>
        console.error("[Socket] Connection error:", err.message)
      );

      socketInstance.on("online_list", ({ emails }) =>
        setOnlineEmails(emails)
      );
      socketInstance.on("user_online", ({ email }) =>
        setOnlineEmails((p) => [...new Set([...p, email])])
      );
      socketInstance.on("user_offline", ({ email }) =>
        setOnlineEmails((p) => p.filter((e) => e !== email))
      );

      // CHANGE 4 — replace pending optimistic message with real server message
      socketInstance.on("receive_message", (msg) => {
        setMessages((prev) => {
          // Remove any pending message that matches this content + sender
          const withoutPending = prev.filter(
            (m) => !(m._pending && m._tempContent === msg.content && m.sender === msg.sender)
          );
          // Deduplicate by _id
          if (withoutPending.find((m) => m._id === msg._id)) return withoutPending;
          return [...withoutPending, msg];
        });
        fetchConversations();
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      });

      socketInstance.on("message_deleted", ({ messageId }) => {
        setMessages((prev) => prev.filter((m) => m._id !== messageId));
      });

      socketInstance.on("typing_indicator", ({ conversationId, email, typing }) => {
        setTypingUsers((prev) => {
          const list = prev[conversationId] || [];
          if (typing)
            return { ...prev, [conversationId]: [...new Set([...list, email])] };
          return { ...prev, [conversationId]: list.filter((e) => e !== email) };
        });
      });

      socketInstance.on("task_notification", () => fetchConversations());
      socketInstance.on("conversation_created", () => fetchConversations());
    });

    return () => {
      if (socketInstance) socketInstance.disconnect();
      socketRef.current = null;
    };
  }, [currentUserEmail, fetchConversations]);

  // ── Open a conversation ───────────────────────────────────────────────────
  const openConversation = useCallback(async (convId) => {
    setActiveConvId(convId);
    setMessages([]);
    setHasMore(false);
    setOldestId(null);
    setLoadingMsgs(true);

    // CHANGE 2 — join room immediately so socket is ready before any send
    socketRef.current?.emit("join_room", { conversationId: convId });

    try {
      const tok = await getToken();
      if (!tok) return;

      const res = await axios.get(`${API_URL}/chat/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setMessages(res.data.data || []);
      setHasMore(res.data.hasMore);
      setOldestId(res.data.oldestId);

      await axios.patch(`${API_URL}/chat/conversations/${convId}/read`, {}, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      socketRef.current?.emit("message_read", { conversationId: convId });
      fetchConversations();
    } catch (err) {
      console.error("openConversation:", err.message);
    } finally {
      setLoadingMsgs(false);
    }
  }, [fetchConversations, getToken]);

  // ── Start direct conversation with an employee ────────────────────────────
  const startDirect = async (targetEmail) => {
    try {
      const tok = await getToken();
      if (!tok) return;
      const res = await axios.post(
        `${API_URL}/chat/conversations/direct`,
        { targetEmail },
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      const conv = res.data.data;
      setConversations((prev) =>
        prev.find((c) => c._id === conv._id) ? prev : [conv, ...prev]
      );
      openConversation(conv._id);
      setSidebarOpen(false);
    } catch (err) {
      console.error("startDirect:", err.message);
    }
  };

  // ── Auto scroll to bottom ─────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Load older messages ───────────────────────────────────────────────────
  const loadOlder = async () => {
    if (!hasMore || !oldestId) return;
    try {
      const tok = await getToken();
      if (!tok) return;
      const res = await axios.get(
        `${API_URL}/chat/conversations/${activeConvId}/messages?before=${oldestId}`,
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      setMessages((prev) => [...(res.data.data || []), ...prev]);
      setHasMore(res.data.hasMore);
      setOldestId(res.data.oldestId);
    } catch (err) {
      console.error("loadOlder:", err.message);
    }
  };

  // CHANGE 3 — optimistic send: show message immediately, replace on echo
  const sendMessage = () => {
    if (!inputText.trim() || !activeConvId) return;
    const content = inputText.trim();

    const pending = {
      _id:          `pending-${Date.now()}`,
      _pending:     true,
      _tempContent: content,
      conversationId: activeConvId,
      sender:       currentUserEmail,
      content,
      type:         "text",
      createdAt:    new Date().toISOString(),
      readBy:       [currentUserEmail],
    };

    setMessages((prev) => [...prev, pending]);
    setInputText("");
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

    socketRef.current?.emit("send_message", { conversationId: activeConvId, content });
    socketRef.current?.emit("typing_stop",  { conversationId: activeConvId });
  };

  // ── Typing indicator ──────────────────────────────────────────────────────
  const handleInputChange = (val) => {
    setInputText(val);
    if (!activeConvId) return;
    socketRef.current?.emit("typing_start", { conversationId: activeConvId });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      socketRef.current?.emit("typing_stop", { conversationId: activeConvId });
    }, 2000);
  };

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConvId) return;
    e.target.value = "";

    setUploading(true);
    try {
      const tok = await getToken();
      if (!tok) return;
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/chat/upload`, formData, {
        headers: {
          Authorization: `Bearer ${tok}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const { fileUrl, fileName, fileSize, fileMimeType } = res.data.data;
      socketRef.current?.emit("file_message", {
        conversationId: activeConvId, fileUrl, fileName, fileSize, fileMimeType,
      });
    } catch (err) {
      alert(err.response?.data?.message || "File upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // ── Delete message ────────────────────────────────────────────────────────
  const deleteMessage = (messageId) => {
    socketRef.current?.emit("delete_message", { messageId, conversationId: activeConvId });
  };

  // ── Active conversation data ──────────────────────────────────────────────
  const activeConv = conversations.find((c) => c._id === activeConvId);

  const convName = (conv) => {
    if (!conv) return "";
    if (conv.type === "group") return conv.name;
    const other = conv.members.find((m) => m !== currentUserEmail);
    return other || conv.members[0];
  };

  const convIsOnline = (conv) => {
    if (!conv || conv.type === "group") return false;
    const other = conv.members.find((m) => m !== currentUserEmail);
    return onlineEmails.includes(other);
  };

  const typingList = (typingUsers[activeConvId] || []).filter((e) => e !== currentUserEmail);

  // ── Sidebar conversations filtered by search ──────────────────────────────
  const filteredConvs = conversations.filter((c) =>
    convName(c).toLowerCase().includes(convSearch.toLowerCase())
  );

  const groups  = filteredConvs.filter((c) => c.type === "group");
  const directs = filteredConvs.filter((c) => c.type === "direct");

  const employeesWithoutConv = allEmployees.filter(
    (e) =>
      e !== currentUserEmail &&
      e.toLowerCase().includes(convSearch.toLowerCase()) &&
      !conversations.find(
        (c) => c.type === "direct" && c.members.includes(e)
      )
  );

  return (
    <div className="chat-shell">

      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <div className={`chat-sidebar ${sidebarOpen ? "open" : "closed"}`}>

        <div className="chat-sidebar-top">
          <div className="chat-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="chat-search-icon" />
            <input
              className="chat-search-input"
              placeholder="Search people or groups..."
              value={convSearch}
              onChange={(e) => setConvSearch(e.target.value)}
            />
            {convSearch && (
              <button className="chat-search-clear" onClick={() => setConvSearch("")}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
          <button className="new-group-btn" onClick={() => setShowNewGroup(true)} title="New Group">
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        <div className="chat-sidebar-list">

          {groups.length > 0 && (
            <>
              <div className="sidebar-section-label">Groups</div>
              {groups.map((conv) => (
                <div
                  key={conv._id}
                  className={`conv-item ${activeConvId === conv._id ? "active" : ""}`}
                  onClick={() => { openConversation(conv._id); setSidebarOpen(false); }}
                >
                  <Avatar email={conv.name} size={38} isGroup />
                  <div className="conv-item-body">
                    <div className="conv-item-name">{conv.name}</div>
                    <div className="conv-item-preview">{conv.lastMessage || "No messages yet"}</div>
                  </div>
                  {conv.unreadCount > 0 && (
                    <span className="unread-badge">{conv.unreadCount}</span>
                  )}
                </div>
              ))}
            </>
          )}

          {directs.length > 0 && (
            <>
              <div className="sidebar-section-label">Direct Messages</div>
              {directs.map((conv) => {
                const other = conv.members.find((m) => m !== currentUserEmail);
                const online = onlineEmails.includes(other);
                return (
                  <div
                    key={conv._id}
                    className={`conv-item ${activeConvId === conv._id ? "active" : ""}`}
                    onClick={() => { openConversation(conv._id); setSidebarOpen(false); }}
                  >
                    <div style={{ position: "relative" }}>
                      <Avatar email={other} size={38} />
                      <span className="online-dot-avatar">
                        <OnlineDot online={online} />
                      </span>
                    </div>
                    <div className="conv-item-body">
                      <div className="conv-item-name">{other}</div>
                      <div className="conv-item-preview">{conv.lastMessage || "Start a conversation"}</div>
                    </div>
                    {conv.unreadCount > 0 && (
                      <span className="unread-badge">{conv.unreadCount}</span>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {employeesWithoutConv.length > 0 && (
            <>
              <div className="sidebar-section-label">All Employees</div>
              {employeesWithoutConv.map((email) => (
                <div
                  key={email}
                  className="conv-item"
                  onClick={() => startDirect(email)}
                >
                  <div style={{ position: "relative" }}>
                    <Avatar email={email} size={38} />
                    <span className="online-dot-avatar">
                      <OnlineDot online={onlineEmails.includes(email)} />
                    </span>
                  </div>
                  <div className="conv-item-body">
                    <div className="conv-item-name">{email}</div>
                    <div className="conv-item-preview">Click to start chat</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {filteredConvs.length === 0 && employeesWithoutConv.length === 0 && (
            <div className="sidebar-empty">No results for "{convSearch}"</div>
          )}
        </div>
      </div>

      {/* ── Message Area ───────────────────────────────────────────────── */}
      <div className="chat-main">

        {activeConv ? (
          <>
            <div className="chat-header">
              <button className="chat-back-btn" onClick={() => setSidebarOpen(true)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div style={{ position: "relative" }}>
                <Avatar
                  email={convName(activeConv)}
                  size={38}
                  isGroup={activeConv.type === "group"}
                />
                {activeConv.type === "direct" && (
                  <span className="online-dot-avatar">
                    <OnlineDot online={convIsOnline(activeConv)} />
                  </span>
                )}
              </div>
              <div className="chat-header-info">
                <div className="chat-header-name">{convName(activeConv)}</div>
                <div className="chat-header-sub">
                  {activeConv.type === "group"
                    ? `${activeConv.members.length} members`
                    : convIsOnline(activeConv) ? "Online" : "Offline"}
                </div>
              </div>
            </div>

            <div className="chat-messages">
              {hasMore && (
                <div className="load-more-wrap">
                  <button className="load-more-btn" onClick={loadOlder}>
                    <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: 6 }} />
                    Load older messages
                  </button>
                </div>
              )}

              {loadingMsgs && (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: "var(--text-gray)" }} />
                </div>
              )}

              {messages.map((msg, i) => {
                const isMine = msg.sender === currentUserEmail;
                const showSender = activeConv.type === "group" && !isMine &&
                  (i === 0 || messages[i - 1]?.sender !== msg.sender);
                return (
                  <MessageBubble
                    key={msg._id}
                    message={msg}
                    isMine={isMine}
                    showSender={showSender}
                    onDelete={isMine ? deleteMessage : null}
                    onViewTask={onViewTask}
                  />
                );
              })}

              {typingList.length > 0 && (
                <div className="typing-indicator">
                  <span className="typing-dots">
                    <span /><span /><span />
                  </span>
                  {typingList.join(", ")} {typingList.length === 1 ? "is" : "are"} typing...
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-bar">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />
              <button
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Attach file"
              >
                {uploading
                  ? <FontAwesomeIcon icon={faSpinner} spin />
                  : <FontAwesomeIcon icon={faPaperclip} />}
              </button>
              <input
                className="chat-text-input"
                value={inputText}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                placeholder="Type a message..."
              />
              <button
                className="send-btn"
                onClick={sendMessage}
                disabled={!inputText.trim()}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </>
        ) : (
          <div className="chat-empty-state">
            <FontAwesomeIcon icon={faUsers} className="chat-empty-icon" />
            <h3>Select a conversation</h3>
            <p>Choose a person or group from the left to start chatting.</p>
          </div>
        )}
      </div>

      {showNewGroup && (
        <NewGroupModal
          onClose={() => setShowNewGroup(false)}
          onCreated={(conv) => {
            setConversations((prev) => [conv, ...prev]);
            openConversation(conv._id);
          }}
          allEmployees={allEmployees}
          currentEmail={currentUserEmail}
        />
      )}
    </div>
  );
};

export default ChatsTab;